/**
 * App-wide TanStack Query client. Imported by App.tsx and by tests that need
 * to seed cache state.
 *
 * Defaults documented at:
 * docs/superpowers/specs/2026-04-30-tanstack-query-migration-design.md
 */
import { QueryClient } from '@tanstack/react-query';
import { isApiClientError } from '@/api/errors';

/**
 * Retry transient failures, never retry a definitive HTTP answer.
 *
 * - Network/TLS error (no response — e.g. wifi dropped on wake): the SDK throws
 *   a plain Error, not an ApiClientError. Retry it; this is what self-heals the
 *   page after the internet comes back instead of leaving it stuck/erroring.
 * - 5xx: server responded but transiently failed (cold start, 502). Retry.
 * - 4xx (403 no-permission, 404, 422 validation): a definitive answer that
 *   won't change by retrying. Do NOT retry — this is what once stormed the
 *   invites endpoints with repeated 403s.
 */
const retryTransient = (failureCount: number, error: unknown): boolean => {
  if (isApiClientError(error)) return error.isServerError() && failureCount < 2;
  return failureCount < 2;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      retry: retryTransient,
    },
    mutations: {
      retry: 0,
    },
  },
});
